import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { notificationsAPI, authAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Building2, DoorOpen, CreditCard, Wrench,
  FileText, Package, Bell, Users, ChevronLeft, ChevronRight,
  Search, LogOut, Menu, X, User as UserIcon,
  Shield, UserCog, Calculator, Globe, Lock, FolderOpen, UsersRound, HardHat,
  Landmark, PiggyBank, Settings as SettingsIcon,
} from 'lucide-react';
import { initials, avatarColor } from '../../utils/helpers';
import Modal from '../common/Modal';
import { toast } from '../common/Toast';


// Master list of all nav items in display order
const NAV_ITEMS = [
  { path: '/',                 icon: LayoutDashboard, labelKey: 'dashboard',       section: 'operations' },
  { path: '/properties',       icon: Building2,       labelKey: 'properties',      section: 'operations' },
  { path: '/apartments',       icon: DoorOpen,        labelKey: 'apartments',      section: 'operations' },
  { path: '/payments',         icon: CreditCard,      labelKey: 'payments',        section: 'management' },
  { path: '/work-orders',      icon: Wrench,          labelKey: 'workOrders',      section: 'management' },
  { path: '/leases',           icon: FileText,        labelKey: 'leases',          section: 'management' },
  { path: '/tenants',          icon: UsersRound,      labelKey: 'tenants',         section: 'management' },
  { path: '/inventory',        icon: Package,         labelKey: 'inventory',       section: 'management' },
  { path: '/documents',        icon: FolderOpen,      labelKey: 'documents',       section: 'management' },
  { path: '/expenses',         icon: Landmark,        labelKey: 'expenses',        section: 'finance' },
  { path: '/budget',           icon: PiggyBank,       labelKey: 'budget',          section: 'finance' },
  { path: '/notifications',    icon: Bell,            labelKey: 'notifications',   section: 'admin', badge: true },
  { path: '/users',            icon: Users,           labelKey: 'users',           section: 'admin' },
  { path: '/role-permissions', icon: Shield,          labelKey: 'rolePermissions', section: 'admin' },
  { path: '/settings',         icon: SettingsIcon,    labelKey: 'settings',        section: 'admin' },
];

function buildNav(paths, t) {
  const pathSet = new Set(paths);
  const sections = {};
  NAV_ITEMS.forEach(item => {
    if (!pathSet.has(item.path)) return;
    if (!sections[item.section]) sections[item.section] = [];
    sections[item.section].push(item);
  });
  return Object.entries(sections).map(([section, items]) => ({ section: t(section), items }));
}

export default function AppShell({ children }) {
  const { user, logout, setUser } = useAuth();
  const { navPaths }            = usePermissions();
  const { t, i18n }             = useTranslation();
  const navigate                = useNavigate();
  const location                = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread]       = useState(0);
  const [userDrop, setUserDrop]   = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [pwdForm, setPwdForm]     = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  // Tenant nav is always fixed regardless of DB permissions
  const NAV = useMemo(() => {
    if (user?.role === 'tenant') {
      return [
        { section: t('operations'), items: [{ path: '/', icon: LayoutDashboard, labelKey: 'dashboard' }] },
        { section: t('management'), items: [
          { path: '/payments',    icon: CreditCard,  labelKey: 'payments' },
          { path: '/work-orders', icon: Wrench,      labelKey: 'workOrders' },
          { path: '/leases',      icon: FileText,    labelKey: 'leases' },
          { path: '/documents',   icon: FolderOpen,  labelKey: 'documents' },
        ]},
      ];
    }
    return buildNav(navPaths, t);
  }, [user?.role, navPaths, t, i18n.language]);

  useEffect(() => {
    notificationsAPI.getAll({ read: false })
      .then(r => setUnread(r.data.unreadCount || 0))
      .catch(() => {});
  }, [location.pathname]);

  // Switching language also stores the choice on the user record. The browser
  // keeps its own copy in localStorage, but the rent-reminder job runs on the
  // server with no browser to ask, and has to know which language to write the
  // digest in. A failure here is silent on purpose: the UI has already changed
  // language, and a toast about a preference the user cannot act on is noise.
  const pickLanguage = async (lng) => {
    if (i18n.language === lng) return;
    i18n.changeLanguage(lng);
    try {
      const { data } = await authAPI.setLanguage(lng);
      if (data?.user) { setUser(data.user); localStorage.setItem('user', JSON.stringify(data.user)); }
    } catch { /* language still changed locally */ }
  };

  const go = path => { navigate(path); setMobileOpen(false); };
  const isActive = path => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const roleIcon = { admin: Shield, manager: UserCog, maintenance: Wrench, accountant: Calculator, contractor: HardHat, tenant: UserIcon }[user?.role] || UserIcon;
  const RoleIcon = roleIcon;

  const openChangePwd = () => {
    setUserDrop(false);
    setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setChangePwdOpen(true);
  };

  const handleChangePwd = async () => {
    if (!pwdForm.currentPassword || !pwdForm.newPassword) {
      return toast.error(t('fillPasswordFields'));
    }
    if (pwdForm.newPassword.length < 6) {
      return toast.error(t('min6Characters'));
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      return toast.error(t('passwordsDoNotMatch'));
    }
    setPwdSaving(true);
    try {
      await authAPI.changePassword({ currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      toast.success(t('passwordUpdated'));
      setChangePwdOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || t('failedUpdatePassword'));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="app-shell">
      {/* Mobile overlay */}
      <div className={`mobile-overlay${mobileOpen ? ' show' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* ── Sidebar ── */}
      <aside className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
        {/* Logo */}
        <div style={{ padding: '18px 14px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Building2 size={19} color="#fff" />
          </div>
          {!collapsed && (
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: '#fff', lineHeight: 1.1 }}>PropManager</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '.05em' }}>{t('propertyManagementTag')}</div>
            </div>
          )}
          <button className="btn btn-ghost btn-icon" onClick={() => setMobileOpen(false)}
            style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.3)', display: 'none' }} id="sidebar-close">
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {NAV.map(({ section, items }) => (
            <div key={section}>
              {!collapsed && <div className="nav-section-label">{section}</div>}
              {collapsed && <div style={{ height: 8 }} />}
              {items.map(({ path, icon: Icon, labelKey, badge }) => (
                <div key={path} className={`nav-item${isActive(path) ? ' active' : ''}`}
                  onClick={() => go(path)} title={collapsed ? t(labelKey) : undefined}>
                  <Icon size={19} style={{ flexShrink: 0 }} />
                  {!collapsed && <span style={{ flex: 1 }}>{t(labelKey)}</span>}
                  {!collapsed && badge && unread > 0 && <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>}
                  {collapsed && badge && unread > 0 && (
                    <span style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, background: 'var(--rose)', borderRadius: '50%', border: '2px solid var(--sidebar-bg)' }} />
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '10px 8px 8px' }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 8px', marginBottom: 4 }}>
              <div className="avatar avatar-sm" style={{ background: avatarColor(user?.name), color: '#fff', fontWeight: 800, flexShrink: 0 }}>
                {initials(user?.name)}
              </div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <RoleIcon size={10} /> {user?.role}
                </div>
              </div>
            </div>
          )}
          <button className="btn btn-ghost btn-icon" onClick={() => { logout(); navigate('/login'); }}
            style={{ color: 'rgba(255,255,255,0.35)', width: '100%', justifyContent: 'center', gap: 8 }}
            title={t('signOut')}>
            <LogOut size={16} />
            {!collapsed && <span style={{ fontSize: 13 }}>{t('signOut')}</span>}
          </button>
          <button className="btn btn-ghost btn-icon" onClick={() => setCollapsed(c => !c)}
            style={{ color: 'rgba(255,255,255,0.25)', width: '100%', justifyContent: 'center', marginTop: 2 }}>
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className={`main-content${collapsed ? ' collapsed' : ''}`}>
        {/* Topbar */}
        <header className="topbar">
          <button className="btn btn-ghost btn-icon" style={{ color: 'var(--ink-40)' }}
            onClick={() => setMobileOpen(o => !o)} id="mobile-menu">
            <Menu size={20} />
          </button>

          <div className="search-bar" style={{ flex: 1, maxWidth: 380 }}>
            <Search size={15} color="var(--ink-20)" />
            <input placeholder={t('searchPlaceholder')} />
          </div>

          {/* Notif bell */}
          <button className="btn btn-ghost btn-icon" style={{ position: 'relative', color: 'var(--ink-40)' }}
            onClick={() => go('/notifications')}>
            <Bell size={20} />
            {unread > 0 && (
              <span style={{ position: 'absolute', top: 4, right: 4, width: 17, height: 17, background: 'var(--rose)', color: '#fff', borderRadius: '50%', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          {/* User dropdown */}
          <div className="dropdown">
            <button className="btn btn-ghost" style={{ gap: 8, padding: '5px 10px' }} onClick={() => setUserDrop(o => !o)}>
              <div className="avatar avatar-sm" style={{ background: avatarColor(user?.name), color: '#fff', fontWeight: 800 }}>
                {initials(user?.name)}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-60)' }} className="mobile-hide">{user?.name?.split(' ')[0]}</span>
            </button>
            {userDrop && (
              <div className="dropdown-menu" style={{ minWidth: 220 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{user?.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{user?.email}</div>
                  <span className="badge badge-brand" style={{ marginTop: 6 }}>{user?.role}</span>
                </div>
                <div className="dropdown-item" onClick={() => { setUserDrop(false); go('/notifications'); }}>
                  <Bell size={15} /> {t('notifications')} {unread > 0 && <span className="nav-badge">{unread}</span>}
                </div>
                <div className="dropdown-item" onClick={openChangePwd}>
                  <Lock size={15} /> {t('changePassword')}
                </div>
                <div className="dropdown-divider" />
                <div className="dropdown-item" onClick={() => pickLanguage('en')}>
                  <Globe size={15} /> {t('english')}
                </div>
                <div className="dropdown-item" onClick={() => pickLanguage('es')}>
                  <Globe size={15} /> {t('spanish')}
                </div>
                <div className="dropdown-divider" />
                <div className="dropdown-item" style={{ color: 'var(--rose)' }}
                  onClick={() => { setUserDrop(false); logout(); navigate('/login'); }}>
                  <LogOut size={15} /> {t('logout')}
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="page-content" onClick={() => userDrop && setUserDrop(false)}>
          {children}
          <div style={{ marginTop: 40, paddingTop: 16, borderTop: '1px solid var(--border)', textAlign: 'center', fontSize: 11, color: 'var(--ink-20)', letterSpacing: '.04em' }}>
            Developed by{' '}
            <a href="https://mercurysoftwaresolutions.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ink-40)', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.textDecoration = 'underline'}
              onMouseLeave={e => e.target.style.textDecoration = 'none'}>
              Mercury Software Solutions
            </a>
            {' '}@2026
          </div>
        </div>
      </main>

      {/* Change Password Modal */}
      <Modal open={changePwdOpen} onClose={() => setChangePwdOpen(false)} title={t('changePassword')} size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setChangePwdOpen(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={handleChangePwd} disabled={pwdSaving}>
              {pwdSaving ? t('saving') : t('updatePassword')}
            </button>
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">{t('currentPassword')}</label>
            <input
              className="form-input"
              type="password"
              value={pwdForm.currentPassword}
              onChange={e => setPwdForm(f => ({ ...f, currentPassword: e.target.value }))}
              placeholder={t('enterCurrentPassword')}
              autoComplete="current-password"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('newPassword')}</label>
            <input
              className="form-input"
              type="password"
              value={pwdForm.newPassword}
              onChange={e => setPwdForm(f => ({ ...f, newPassword: e.target.value }))}
              placeholder={t('atLeast6Characters') || t('min6Characters')}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('confirmNewPassword')}</label>
            <input
              className="form-input"
              type="password"
              value={pwdForm.confirmPassword}
              onChange={e => setPwdForm(f => ({ ...f, confirmPassword: e.target.value }))}
              placeholder={t('repeatNewPassword')}
              autoComplete="new-password"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
